import { NetworkTabLayoutComponent } from './../../../../network/layouts/network-tab-layout/network-tab-layout.component';
import { NetworkStandardLayoutComponent } from '../../../../network/layouts/network-standard-layout/network-standard-layout.component';
import { RouteGuard } from '../../../guards/route-guard/route-guard';
import { GridStateResolver } from '../../../resolvers/grid-state/grid-state.resolver';
import { MaintenanceModeResolver } from '../../../resolvers/maintenance-mode/maintenance-mode.resolver';
import { NetworkSideNavResolver } from '../../../resolvers/network-side-nav/network-side-nav.resolver';
import { deprecatedNetworkRoutesMatcher } from '../../../url-matchers/deprecated-network-routes-matcher/deprecated-network-routes-matcher';
import { ClusterIdResolver } from '../../../resolvers/cluster-id/cluster-id.resolver';
import { SyncCombinatorGuard } from 'Src/ng2/routing/guards/sync-combinator-guard/sync-combinator-guard';
import { TogglesGuard } from 'Src/ng2/routing/guards/toggles-guard/toggles-guards';
import { NetworkFullScreenLayoutComponent } from 'Src/ng2/network/layouts/network-full-screen-layout/network-full-screen-layout.component';
import { CLUSTER_TYPES } from 'Src/ng2/shared/typings/interfaces/cluster.interface';
import { PartnerTypes } from 'Src/ng2/shared/typings/interfaces/partner.interface';
import { NonEntityUserGuard } from 'Src/ng2/routing/guards/non-entity-user-guard/non-entity-user-guard';
import { OpNetworkHomeRedirectGuard } from 'Src/ng2/routing/guards/op-network-home-redirect-guard/op-network-home-redirect-guard';
import { OpNetworkEvaluationGuard } from 'Src/ng2/routing/guards/op-network-evaluation-guard/op-network-evaluation-guard';
import { CurrentEntityGuard } from 'Src/ng2/routing/guards/current-entity-guard/current-entity-guard';
import { FeatureSet, PageTemplate } from 'Src/ng2/shared/typings/interfaces/mixpanel.interface';
/**
 * Guards that evaluate against one cluster, declared on the `:clusterId` routes rather than on the
 * parameterless `network` parent. Angular reuses a route whose own params have not changed, so a
 * chain held on the parent does not re-run when only the cluster changes — a child-to-child switch
 * or a history navigation would keep the outgoing network's store and toggle evaluation.
 *
 * Order matters: SyncCombinatorGuard awaits these in sequence, and the parent's guards resolve
 * before any of them. OpNetworkHomeRedirectGuard is last because it reads the evaluation
 * OpNetworkEvaluationGuard writes rather than running the formula itself.
 */
const clusterScopedGuards = [CurrentEntityGuard, TogglesGuard, OpNetworkEvaluationGuard, OpNetworkHomeRedirectGuard];

export const networkRoute = {
  path: 'network',
  url: '/network',
  canActivate: [SyncCombinatorGuard],
  data: {
    // Context-free: these decide whether the user may be in the network tree at all, which no
    // cluster change alters. Order matters: SyncCombinatorGuard awaits these in sequence.
    guards: [RouteGuard, NonEntityUserGuard],
  },
  children: [
    // matches old route and redirects to /home
    {
      matcher: deprecatedNetworkRoutesMatcher,
      redirectTo: '/home',
    },
    // START OF NEW ROUTE URL:
    // new route for school net dash: added cluster type and cluster id
    {
      path: 'school/:clusterId',
      canActivate: [SyncCombinatorGuard],
      resolve: {
        cluster: ClusterIdResolver,
      },
      data: {
        clusterType: CLUSTER_TYPES.SCHOOL,
        partnerType: PartnerTypes.SCHOOL_NETWORK,
        guards: clusterScopedGuards,
      },
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'dashboard',
        },
        {
          path: 'dashboard',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Home,
            pageTemplate: PageTemplate.Home,
          },
          loadChildren: () =>
            import('../../../../network/network-top-level/network-top-level.module').then(m => m.NetworkTopLevelModule),
        },
        {
          path: 'school-profile',
          component: NetworkFullScreenLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Profile,
          },
          loadChildren: () =>
            import('../../../../network/school-profile/school-profile.module').then(m => m.SchoolProfileModule),
        },
        {
          path: 'pass-rates',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.PassRates,
          },
          loadChildren: () =>
            import('../../../../network/network-pass-rates/network-pass-rates.module').then(
              m => m.NetworkPassRatesModule,
            ),
        },
        {
          path: 'regents-results',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.RegentsResults,
          },
          loadChildren: () =>
            import('../../../../network/network-regents-results/network-regents-results.module').then(
              m => m.NetworkRegentsResultsModule,
            ),
        },
        {
          path: 'family-needs',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.ECFIK,
          },
          loadChildren: () =>
            import('../../../../network/network-family-needs/network-family-needs.module').then(
              m => m.NetworkFamilyNeedsModule,
            ),
        },
        {
          path: 'midlevel',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            // No featureSet to be mapped to: the mid-level's area follows the selected focus, so no route-level value is correct.
            pageTemplate: PageTemplate.Breakdown,
          },
          loadChildren: () =>
            import('../../../../network/network-mid-level/network-mid-level.module').then(m => m.NetworkMidLevelModule),
        },
        {
          path: 'studentLevel',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Grid,
          },
          loadChildren: () =>
            import('../../../../network/network-student-level/network-student-level.module').then(
              m => m.NetworkStudentLevelModule,
            ),
        },
        {
          path: 'fociList',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: { maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'] },
          loadChildren: () =>
            import('../../../../network/network-foci-list/network-foci-list.module').then(
              m => m.NetworkFociListModule,
            ),
        },
        {
          path: 'studentFociList',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: { maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'] },
          loadChildren: () =>
            import('../../../../network/network-foci-list/network-student-foci-list.module').then(
              m => m.NetworkStudentFociModule,
            ),
        },
        {
          path: 'student',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Profile,
          },
          loadChildren: () =>
            import('../../../../network/network-student-profile/network-student-profile.module').then(
              m => m.NetworkStudentProfileModule,
            ),
        },
        {
          path: 'grid',
          component: NetworkFullScreenLayoutComponent,
          resolve: { gridState: GridStateResolver, maintenanceMode: MaintenanceModeResolver },
          data: {
            contextPartnerType: PartnerTypes.SCHOOL_NETWORK,
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Grid,
          },
          // loadChildren: () => import('../../../../network/network-foci-grid/network-foci-grid.module').then(m => m.NetworkFociGridModule),
          loadChildren: () =>
            import('../../../../school/server-side-grid/server-side-grid.module').then(m => m.ServerSideGridModule),
        },
        {
          path: 'settings',
          loadChildren: () =>
            import('../../../../network/network-settings/network-settings.module').then(m => m.NetworkSettingsModule),
        },
        {
          path: 'attendance-calendar',
          component: NetworkTabLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            // PageTemplate has no calendar member; declaring featureSet alone still resolves.
            featureSet: FeatureSet.Attendance,
          },
          loadChildren: () =>
            import('../../../../network/network-attendance-calendar/network-attendance-calendar-routing.module').then(
              m => m.NetworkAttendanceCalendarRoutingModule,
            ),
        },
        {
          path: 'postsecondary-dashboard',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Postsecondary,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../../../../network/network-postsecondary-dashboard/network-postsecondary-dashboard-routing.module').then(
              m => m.NetworkPostsecondaryDashboardRoutingModule,
            ),
        },
        {
          path: 'kims-dashboard',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.PostsecondaryKims,
            pageTemplate: PageTemplate.Dashboard,
          },
          loadChildren: () =>
            import('../../../../network/network-kims-dashboard/network-kims-dashboard-routing.module').then(
              m => m.NetworkKimsDashboardRoutingModule,
            ),
        },
      ],
    },
    // shelter net dash
    {
      path: 'shelter/:clusterId',
      canActivate: [SyncCombinatorGuard],
      resolve: {
        cluster: ClusterIdResolver,
      },
      data: {
        clusterType: CLUSTER_TYPES.SHELTER,
        partnerType: PartnerTypes.SHELTER_NETWORK,
        guards: clusterScopedGuards,
      },
      children: [
        {
          path: '',
          pathMatch: 'full',
          redirectTo: 'dashboard',
        },
        {
          path: 'dashboard',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Home,
            pageTemplate: PageTemplate.Home,
          },
          loadChildren: () =>
            import('../../../../network/network-top-level/shelter-network-top-level.module').then(m => m.ShelterNetworkTopLevelModule),
        },
        {
          path: 'midlevel',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            // No featureSet: the mid-level's area follows the selected focus, so no route-level value is correct.
            pageTemplate: PageTemplate.Breakdown,
          },
          loadChildren: () =>
            import('../../../../network/network-mid-level/shelter-network-mid-level.module').then(m => m.ShelterNetworkMidLevelModule),
        },
        {
          path: 'studentLevel',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Grid,
          },
          loadChildren: () =>
            import('../../../../network/network-student-level/shelter-network-student-level.module').then(
              m => m.ShelterNetworkStudentLevelModule,
            ),
        },
        {
          path: 'grid',
          component: NetworkFullScreenLayoutComponent,
          resolve: { gridState: GridStateResolver, maintenanceMode: MaintenanceModeResolver },
          data: {
            contextPartnerType: PartnerTypes.SHELTER_NETWORK,
            maintenanceToggles: ['POSTGRES_MAINTENANCE_IN_PROGRESS'],
            featureSet: FeatureSet.Grid,
          },
          loadChildren: () =>
            import('../../../../school/server-side-grid/server-side-grid.module').then(m => m.ServerSideGridModule),
        },
        {
          path: 'settings',
          loadChildren: () =>
            import('../../../../shelter/shelter-network-settings/shelter-network-settings.module').then(
              m => m.ShelterNetworkSettingsModule,
            ),
        },
        {
          path: 'user-permissions',
          component: NetworkStandardLayoutComponent,
          resolve: { maintenanceMode: MaintenanceModeResolver, networkSideNavConfig: NetworkSideNavResolver },
          data: {
            featureSet: FeatureSet.UserManagement,
          },
          loadChildren: () =>
            import('../../../../shared/components/settings/user-management-settings/user-management-multi-entity-routing.module').then(m => m.UserManagementMultiEntityRoutingModule),
        },
      ],
    },
  ],
};
